import { PluginOptions, TYPE, useToast } from "vue-toastification";

type ToastType = TYPE | string;

interface ToastManager {
  push: (type: ToastType, message: string, opts?: PluginOptions) => void;
  once: (type: ToastType, message: string, opts?: PluginOptions) => void;
  trigger: () => void;
}

let instance: ToastManager | null = null;

export default function useToastManager(): ToastManager {
  if (instance) return instance;

  const toast = useToast();
  const toastQueue: (() => void)[] = [];
  const displayedMessages = new Set<string>();

  const push = (type: ToastType, message: string, opts: PluginOptions = {}): void => {
    const messageId = `${type}:${message}`;

    if (!displayedMessages.has(messageId)) {
      let toastFunction: any;

      switch (type) {
        case TYPE.ERROR:
        case TYPE.WARNING:
          toastFunction = (msg: string, options: PluginOptions) =>
            toast[type](msg, {
              timeout: 10000,
              ...options,
            });
          break;
        case TYPE.INFO:
        case TYPE.SUCCESS:
          toastFunction = toast[type];
          break;
        default:
          toastFunction = toast;
      }

      toastQueue.push(() => toastFunction(message, opts));
      displayedMessages.add(messageId);
    }
  };

  const trigger = (): void => {
    if (toastQueue.length > 0) {
      const show = toastQueue.shift();
      show?.();

      setTimeout(trigger, 1000);
    } else {
      displayedMessages.clear();
    }
  };

  const once = (type: ToastType, message: string, opts?: PluginOptions): void => {
    push(type, message, opts);
    trigger();
  };

  instance = {
    push,
    trigger,
    once,
  };

  return instance;
}
